export function hslToHex(value: string) {
  const parts = value.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0]
  const [h, s, l] = [parts[0], (parts[1] ?? 0) / 100, (parts[2] ?? 0) / 100]
  const a = s * Math.min(l, 1 - l)
  const channel = (n: number) => {
    const k = (n + h / 30) % 12
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
      .toString(16)
      .padStart(2, '0')
  }
  return '#' + channel(0) + channel(8) + channel(4)
}

export function hexToHsl(hex: string) {
  const channels = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255)
  const [r, g, b] = channels
  const max = Math.max(...channels),
    min = Math.min(...channels),
    delta = max - min
  const l = (max + min) / 2
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))
  const rawHue =
    delta === 0
      ? 0
      : max === r
        ? ((g - b) / delta) % 6
        : max === g
          ? (b - r) / delta + 2
          : (r - g) / delta + 4
  const h = Math.round((rawHue * 60 + 360) % 360)
  return h + ' ' + Math.round(s * 100) + '% ' + Math.round(l * 100) + '%'
}

function hslToRgb(value: string): [number, number, number] {
  const channels = (hslToHex(value).slice(1).match(/../g) ?? []).map(
    (channel) => Number.parseInt(channel, 16) / 255,
  )
  return [channels[0] ?? 0, channels[1] ?? 0, channels[2] ?? 0]
}

function relativeLuminance(value: string) {
  const [r, g, b] = hslToRgb(value).map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Razão de contraste WCAG entre duas cores HSL no formato "H S% L%". */
export function contrastRatio(foreground: string, background: string) {
  const [lighter, darker] = [relativeLuminance(foreground), relativeLuminance(background)].sort(
    (a, b) => b - a,
  )
  return (lighter + 0.05) / (darker + 0.05)
}

const LIGHT_TEXT = '0 0% 100%'
const DARK_TEXT = '222 31% 15%'

/** Texto (branco ou quase preto) que melhor se lê sobre todas as cores de fundo informadas. */
export function readableTextFor(...backgrounds: string[]) {
  const worst = (text: string) => Math.min(...backgrounds.map((bg) => contrastRatio(text, bg)))
  return worst(LIGHT_TEXT) >= worst(DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT
}

/** Converte "#0E7490" (com ou sem #, 6 dígitos) para o formato interno; devolve null se não for uma cor válida. */
export function parseHexColor(value: string) {
  const normalized = value.trim().replace(/^#/, '')
  return /^[0-9a-fA-F]{6}$/.test(normalized) ? hexToHsl(`#${normalized.toLowerCase()}`) : null
}
